import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { proposalsApi, Proposal, ProposalSlot } from '../services/api';
import { parseISO, format } from 'date-fns';
import {
    Inbox,
    Clock,
    User,
    Mail,
    MessageSquare,
    Check,
    X,
    Star,
    Calendar,
    Video,
    ArrowLeft,
    Filter
} from 'lucide-react';

export function ProposalsPage() {
    const [searchParams] = useSearchParams();
    const linkIdFilter = searchParams.get('link_id');

    const [proposals, setProposals] = useState<Proposal[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('pending');
    const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);

    // Confirmation modal state
    const [confirmingProposal, setConfirmingProposal] = useState<Proposal | null>(null);
    const [selectedSlotId, setSelectedSlotId] = useState<string>('');
    const [createCalendar, setCreateCalendar] = useState(true);
    const [createTeams, setCreateTeams] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    // Decline modal state
    const [decliningProposal, setDecliningProposal] = useState<Proposal | null>(null);
    const [declineReason, setDeclineReason] = useState('');
    const [isDeclining, setIsDeclining] = useState(false);

    useEffect(() => {
        loadProposals();
    }, [statusFilter, linkIdFilter]);

    const loadProposals = async () => {
        setIsLoading(true);
        try {
            const data = await proposalsApi.list(statusFilter || undefined, linkIdFilter || undefined);
            setProposals(data);
        } catch (err) {
            console.error('Failed to load proposals:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!confirmingProposal || !selectedSlotId) return;

        setIsConfirming(true);
        try {
            const result = await proposalsApi.confirm(
                confirmingProposal.id,
                selectedSlotId,
                createCalendar,
                createTeams
            );
            
            let message = `Meeting confirmed for ${result.confirmed_time}`;
            if (result.teams_link) {
                message += `\n\nTeams link: ${result.teams_link}`;
            }
            alert(message);
            
            setConfirmingProposal(null);
            setSelectedSlotId('');
            setSelectedProposal(null);
            loadProposals();
        } catch (err: any) {
            alert(err.message || 'Failed to confirm proposal');
        } finally {
            setIsConfirming(false);
        }
    };

    const handleDecline = async () => {
        if (!decliningProposal) return;

        setIsDeclining(true);
        try {
            await proposalsApi.decline(decliningProposal.id, declineReason || undefined);
            setDecliningProposal(null);
            setDeclineReason('');
            setSelectedProposal(null);
            loadProposals();
        } catch (err: any) {
            alert(err.message || 'Failed to decline proposal');
        } finally {
            setIsDeclining(false);
        }
    };

    const getStatusBadge = (status: Proposal['status']) => {
        const styles: Record<string, { bg: string; color: string }> = {
            pending: { bg: 'var(--color-primary-light)', color: 'var(--color-primary-hover)' },
            confirmed: { bg: '#dcfce7', color: '#16a34a' },
            declined: { bg: '#fee2e2', color: '#dc2626' },
            expired: { bg: 'var(--color-gray-200)', color: 'var(--color-gray-600)' }
        };
        const style = styles[status] || styles.pending;
        
        return (
            <span style={{
                fontSize: 'var(--font-size-xs)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                background: style.bg,
                color: style.color,
                fontWeight: 500,
                textTransform: 'capitalize'
            }}>
                {status}
            </span>
        );
    };

    const formatSlotTime = (slot: ProposalSlot) => {
        try {
            const start = parseISO(slot.start_time_utc);
            const end = parseISO(slot.end_time_utc);
            return `${format(start, 'EEE, MMM d')} at ${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
        } catch {
            return slot.start_time_utc;
        }
    };

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                        {linkIdFilter && (
                            <Link to="/scheduling-links" className="btn btn-secondary btn-sm">
                                <ArrowLeft size={16} />
                            </Link>
                        )}
                        <h1 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0 }}>
                            <Inbox size={28} />
                            Meeting Proposals
                        </h1>
                    </div>
                    <p style={{ color: 'var(--color-gray-500)' }}>
                        Review and respond to meeting requests
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Filter size={16} style={{ color: 'var(--color-gray-400)' }} />
                    <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ width: 'auto' }}
                    >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="declined">Declined</option>
                    </select>
                </div>
            </div>

            {proposals.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                    <Inbox size={48} style={{ color: 'var(--color-gray-300)', margin: '0 auto var(--space-4)' }} />
                    <h3>No {statusFilter || ''} proposals</h3>
                    <p style={{ color: 'var(--color-gray-500)' }}>
                        {statusFilter === 'pending' 
                            ? 'When someone submits a meeting request through your scheduling link, it will appear here.'
                            : 'No proposals match this filter.'}
                    </p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: selectedProposal ? '1fr 1fr' : '1fr', gap: 'var(--space-4)' }}>
                    {/* Proposals List */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        {proposals.map(proposal => (
                            <div
                                key={proposal.id}
                                className="card"
                                onClick={() => setSelectedProposal(proposal)}
                                style={{
                                    cursor: 'pointer',
                                    borderLeft: selectedProposal?.id === proposal.id 
                                        ? '4px solid var(--color-primary)' 
                                        : '4px solid transparent',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                                            {proposal.requester_name}
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                            {proposal.requester_email}
                                        </div>
                                    </div>
                                    {getStatusBadge(proposal.status)}
                                </div>

                                {proposal.link && (
                                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)', marginBottom: 'var(--space-2)' }}>
                                        <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                        {proposal.link.duration_minutes} min - {proposal.link.name}
                                    </div>
                                )}

                                {proposal.slots && proposal.slots.length > 0 && (
                                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                                        <Star size={12} style={{ display: 'inline', marginRight: '4px', color: 'var(--color-primary)' }} />
                                        {formatSlotTime(proposal.slots[0])}
                                        {proposal.slots.length > 1 && (
                                            <span style={{ color: 'var(--color-gray-400)' }}>
                                                {' '}+{proposal.slots.length - 1} more
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', marginTop: 'var(--space-2)' }}>
                                    {format(parseISO(proposal.created_at), 'MMM d, yyyy \'at\' h:mm a')}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Selected Proposal Detail */}
                    {selectedProposal && (
                        <div className="card" style={{ position: 'sticky', top: 'var(--space-4)', alignSelf: 'start' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-4)' }}>
                                <h3 style={{ margin: 0 }}>Proposal Details</h3>
                                <button 
                                    onClick={() => setSelectedProposal(null)}
                                    className="btn btn-secondary btn-sm"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                    <User size={16} style={{ color: 'var(--color-gray-400)' }} />
                                    <strong>{selectedProposal.requester_name}</strong>
                                    {getStatusBadge(selectedProposal.status)}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-gray-500)', fontSize: 'var(--font-size-sm)' }}>
                                    <Mail size={14} />
                                    <a href={`mailto:${selectedProposal.requester_email}`}>
                                        {selectedProposal.requester_email}
                                    </a>
                                </div>
                            </div>

                            {selectedProposal.message && (
                                <div style={{ 
                                    background: 'var(--color-gray-100)', 
                                    padding: 'var(--space-3)', 
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-4)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                        <MessageSquare size={14} />
                                        Message
                                    </div>
                                    <p style={{ margin: 0 }}>{selectedProposal.message}</p>
                                </div>
                            )}

                            <h4 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                <Clock size={16} />
                                Proposed Times
                            </h4>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                                {selectedProposal.slots?.map((slot, index) => (
                                    <div
                                        key={slot.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: index === 0 ? 'var(--color-primary-light)' : 'var(--color-gray-50)',
                                            borderRadius: 'var(--radius-sm)',
                                            border: index === 0 ? '1px solid var(--color-primary)' : '1px solid var(--color-gray-200)'
                                        }}
                                    >
                                        {index === 0 && <Star size={14} style={{ color: 'var(--color-primary)' }} />}
                                        <span style={{ 
                                            fontSize: 'var(--font-size-xs)', 
                                            color: 'var(--color-gray-500)',
                                            minWidth: '20px'
                                        }}>
                                            #{slot.preference_order}
                                        </span>
                                        <span style={{ flex: 1 }}>{formatSlotTime(slot)}</span>
                                    </div>
                                ))}
                            </div>

                            {selectedProposal.status === 'pending' && (
                                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                                    <button
                                        onClick={() => setDecliningProposal(selectedProposal)}
                                        className="btn btn-secondary"
                                        style={{ flex: 1 }}
                                    >
                                        <X size={18} />
                                        Decline
                                    </button>
                                    <button
                                        onClick={() => {
                                            setConfirmingProposal(selectedProposal);
                                            if (selectedProposal.slots?.[0]) {
                                                setSelectedSlotId(selectedProposal.slots[0].id);
                                            }
                                        }}
                                        className="btn btn-primary"
                                        style={{ flex: 1 }}
                                    >
                                        <Check size={18} />
                                        Confirm
                                    </button>
                                </div>
                            )}

                            {selectedProposal.status === 'confirmed' && selectedProposal.confirmed_slot && (
                                <div style={{
                                    background: '#dcfce7',
                                    padding: 'var(--space-3)',
                                    borderRadius: 'var(--radius-md)',
                                    color: '#16a34a'
                                }}>
                                    <Check size={16} style={{ display: 'inline', marginRight: '8px' }} />
                                    Confirmed for {format(parseISO(selectedProposal.confirmed_slot), 'EEE, MMM d \'at\' h:mm a')}
                                </div>
                            )}

                            {selectedProposal.status === 'declined' && (
                                <div style={{
                                    background: '#fee2e2',
                                    padding: 'var(--space-3)',
                                    borderRadius: 'var(--radius-md)',
                                    color: '#dc2626'
                                }}>
                                    <X size={16} style={{ display: 'inline', marginRight: '8px' }} />
                                    Declined
                                    {selectedProposal.declined_reason && (
                                        <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--font-size-sm)' }}>
                                            Reason: {selectedProposal.declined_reason}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Confirm Modal */}
            {confirmingProposal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: 'var(--space-4)'
                }}>
                    <div className="card" style={{ maxWidth: '450px', width: '100%' }}>
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>Confirm Meeting</h3>

                        <div className="form-group">
                            <label className="form-label">Select Time</label>
                            {confirmingProposal.slots?.map((slot, index) => (
                                <label
                                    key={slot.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        padding: 'var(--space-2)',
                                        borderRadius: 'var(--radius-sm)',
                                        cursor: 'pointer',
                                        background: selectedSlotId === slot.id ? 'var(--color-primary-light)' : 'transparent'
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="slot"
                                        checked={selectedSlotId === slot.id}
                                        onChange={() => setSelectedSlotId(slot.id)}
                                    />
                                    {index === 0 && <Star size={14} style={{ color: 'var(--color-primary)' }} />}
                                    <span>{formatSlotTime(slot)}</span>
                                </label>
                            ))}
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={createCalendar}
                                    onChange={(e) => setCreateCalendar(e.target.checked)}
                                />
                                <Calendar size={16} />
                                Create calendar event
                            </label>
                        </div>

                        {createCalendar && (
                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={createTeams}
                                        onChange={(e) => setCreateTeams(e.target.checked)}
                                    />
                                    <Video size={16} />
                                    Include Teams meeting link
                                </label>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                            <button
                                onClick={() => {
                                    setConfirmingProposal(null);
                                    setSelectedSlotId('');
                                }}
                                className="btn btn-secondary"
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={!selectedSlotId || isConfirming}
                                className="btn btn-primary"
                                style={{ flex: 1 }}
                            >
                                {isConfirming ? 'Confirming...' : 'Confirm Meeting'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Decline Modal */}
            {decliningProposal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: 'var(--space-4)'
                }}>
                    <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>Decline Proposal</h3>

                        <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-4)' }}>
                            This will notify {decliningProposal.requester_name} that their meeting request was declined.
                        </p>

                        <div className="form-group">
                            <label className="form-label">Reason (optional)</label>
                            <textarea
                                className="form-textarea"
                                value={declineReason}
                                onChange={(e) => setDeclineReason(e.target.value)}
                                placeholder="e.g., Schedule conflict, please try again next week..."
                                rows={3}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                            <button
                                onClick={() => {
                                    setDecliningProposal(null);
                                    setDeclineReason('');
                                }}
                                className="btn btn-secondary"
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDecline}
                                disabled={isDeclining}
                                className="btn"
                                style={{ flex: 1, background: 'var(--color-error)', color: 'white' }}
                            >
                                {isDeclining ? 'Declining...' : 'Decline'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
